import type {
  GetState,
  Dispatch,
  ItemBasic,
  ItemDetail,
  AddressPickerSelectedAddress,
} from '@shopee/flow-types';
import callApi from '@shopee/create-api-request';
import { action } from '@shopee/redux-commons';
import getIn from '@shopee/get-in';

import {
  FETCH_PRODUCT,
  FETCH_BATCH_PRODUCT,
  FETCH_ITEM_DETAIL,
  FETCH_PRODUCT_INSTALLMENT_PLAN,
  FETCH_PRODUCT_SHIPPING_BY_ADDRESS,
  FETCH_PRODUCT_SHIPPING,
} from './actionTypes';
import * as api from './api';
import {
  getItemApiProgress,
  getItemById as getItemByIdDefault,
  selectProductShippingInfoV5,
} from './selectors';
import PROGRESS from '@shopee/api-progress';
import { API_STATUS } from '@shopee/flow-types';

type Options = {
  headers?: { [name: string]: string };
  forceFetch?: boolean;
  fromFlashSale?: boolean;
};

export function fetchItem(
  shopId: number,
  itemId: number,
  { headers = {}, forceFetch = false }: Options = {}
) {
  return async (dispatch: Dispatch, getState: GetState) => {
    return await callApi(
      {
        apiCall: () => {
          return api.fetchProductDetailV4(shopId, itemId, { headers });
        },
        actions: [
          {
            type: FETCH_PRODUCT.REQUESTED,
            payload: () => {
              return { itemId };
            },
          },
          {
            type: FETCH_PRODUCT.SUCCESS,
            payload: (state, action, res) => {
              return {
                itemId,
                item: res.data || res.item,
              };
            },
          },
          {
            type: FETCH_PRODUCT.FAILED,
            payload: (state, action, res) => {
              return {
                itemId,
                error: res.error,
              };
            },
          },
        ],
        shouldSkipCall: state =>
          !forceFetch && !!getItemByIdDefault(state, itemId),
      },
      dispatch,
      getState
    );
  };
}

export function fetchBatchItemV4(
  itemShopIds: {
    item_id: number;
    shop_id: number;
  }[],
  source: string,
  onFinish?: () => void
) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const itemIds = itemShopIds.map(itemShopId => itemShopId.item_id);
    return await callApi(
      {
        apiCall: () => api.fetchBatchProductV4(itemShopIds, source),
        actions: [
          {
            type: FETCH_BATCH_PRODUCT.REQUESTED,
            payload: () => ({
              itemIds,
            }),
          },
          {
            type: FETCH_BATCH_PRODUCT.SUCCESS,
            payload: (state, action, res) => ({
              items: res.items,
            }),
          },
          {
            type: FETCH_BATCH_PRODUCT.FAILED,
            payload: (state, action, res) => ({
              itemIds,
              error: res.error,
            }),
          },
        ],
        onFinish,
        shouldSkipCall: () => itemShopIds.length === 0,
      },
      dispatch,
      getState
    );
  };
}

/**
 * Filters out already fetched item Ids before calling API.
 */
export function fetchFilteredBatchItemV4(
  itemShopIds: {
    item_id: number;
    shop_id: number;
  }[],
  source: string,
  onFinish?: () => void,
  customFetchFn?: <R>(fetchFn: () => Promise<R>) => Promise<R>
) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const itemIds = itemShopIds
      .map(itemShopId => itemShopId.item_id)
      .filter(itemId => getItemApiProgress(state, itemId) === undefined);

    const fetchFn = () => api.fetchBatchProductV4(itemShopIds, source);
    return await callApi(
      {
        apiCall: () => {
          return typeof customFetchFn === 'function'
            ? customFetchFn(fetchFn)
            : fetchFn();
        },
        actions: [
          {
            type: FETCH_BATCH_PRODUCT.REQUESTED,
            payload: () => ({
              itemIds,
            }),
          },
          {
            type: FETCH_BATCH_PRODUCT.SUCCESS,
            payload: (state, action, res) => ({
              items: res.items,
            }),
          },
          {
            type: FETCH_BATCH_PRODUCT.FAILED,
            payload: (state, action, res) => ({
              itemIds,
              error: res.error,
            }),
          },
        ],
        onFinish,
        shouldSkipCall: state => {
          const hasItemsBeenFetched = itemIds
            .map(itemId => getItemApiProgress(state, itemId as number))
            .every(
              itemProgress => itemProgress && itemProgress === PROGRESS.OK
            );
          return hasItemsBeenFetched;
        },
      },
      dispatch,
      getState
    );
  };
}

export function fetchBatchItem(
  itemShopIds: {
    itemid: number;
    shopid: number;
    from_flash_sale?: boolean;
  }[],
  onFinish?: () => void,
  preserveInvalid?: boolean, // NOTE: To tell server to return data even if items are invalid
  options: {
    schemaFilter?: string[];
    requestVersion?: string;
  } = {}
) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const isPartial = !!options.schemaFilter;
    const itemIds = itemShopIds.map(itemShopId => itemShopId.itemid);
    return await callApi(
      {
        apiCall: () => {
          return api.fetchBatchProduct(
            itemShopIds,
            preserveInvalid,
            options && options.schemaFilter,
            options.requestVersion
          );
        },
        actions: [
          {
            type: FETCH_BATCH_PRODUCT.REQUESTED,
            payload: () => ({
              itemIds,
              isPartial,
            }),
          },
          {
            type: FETCH_BATCH_PRODUCT.SUCCESS,
            payload: (state, action, res) => {
              return {
                items: res.items,
                isPartial,
              };
            },
          },
          {
            type: FETCH_BATCH_PRODUCT.FAILED,
            payload: (state, action, res) => {
              return {
                error: res.error,
                isPartial,
                itemIds,
              };
            },
          },
        ],
        onFinish,
        shouldSkipCall: () => itemShopIds.length === 0,
      },
      dispatch,
      getState
    );
  };
}

export function fetchItemDetail(
  shopId: number,
  itemId: number,
  options?: { headers: { [name: string]: any } }
) {
  return async function (dispatch: Dispatch) {
    dispatch(action(FETCH_ITEM_DETAIL.REQUESTED, { itemId }));
    const { response, error } = await api.fetchProductDetailV4(
      shopId,
      itemId,
      options
    );

    let item;
    if (response) {
      const captcha = getIn(response, ['captcha'], null);
      if (captcha && captcha.scene) {
        item = {
          captcha,
          challenge_type: response?.challenge_type || 0,
        };
      } else {
        item = response.data || response.item;
      }
    }
    if (!error) {
      dispatch(
        action(FETCH_ITEM_DETAIL.SUCCESS, {
          itemId,
          item: {
            ...item,
            version: response && response.version,
          },
        })
      );
    } else {
      dispatch(action(FETCH_ITEM_DETAIL.FAILED, { itemId, error }));
    }
    return { error, item };
  };
}

// eslint-disable-next-line max-params
export function fetchItemInstallmentPlan(
  shopId: number,
  itemId: number,
  modelId?: number | null,
  isBFFEnabledForUser?: boolean,
  options?: {
    apm?: { apm_pid: string; apm_ts: number; apm_p: string; apm_fs?: boolean };
  }
) {
  return async function (dispatch: Dispatch) {
    dispatch(action(FETCH_PRODUCT_INSTALLMENT_PLAN.REQUESTED, { itemId }));
    const { response, error } = await api.fetchProductInstallmentPlan(
      shopId,
      itemId,
      modelId,
      isBFFEnabledForUser,
      options
    );
    if (!error) {
      dispatch(
        action(FETCH_PRODUCT_INSTALLMENT_PLAN.SUCCESS, {
          itemId,
          plans: response && response.data,
        })
      );
    } else {
      dispatch(action(FETCH_PRODUCT_INSTALLMENT_PLAN.FAILED, { itemId }));
    }
    return { error, plans: response && response.data };
  };
}

export function fetchProductShippingByAddress(
  shopId: number,
  itemId: number,
  address: AddressPickerSelectedAddress,
  isLite = false
) {
  return async (dispatch: Dispatch) => {
    dispatch(
      action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.REQUESTED, {
        payload: { shopId, itemId, address },
      })
    );
    const { response, error } = await api.getShippingFeeByAddress(
      shopId,
      itemId,
      address,
      isLite
    );

    if (
      !error &&
      response &&
      response.shipping_infos &&
      response.promotion_rules
    ) {
      dispatch(
        action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.SUCCESS, {
          payload: { shopId, itemId, shipping: response },
        })
      );
    } else {
      dispatch(
        action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.FAILED, {
          payload: { shopId, itemId, error: error },
        })
      );
    }
  };
}

export function fetchProductShippingByAddressV4(
  shopId: number,
  itemId: number,
  address: AddressPickerSelectedAddress,
  isLite = false
) {
  return async (dispatch: Dispatch) => {
    dispatch(
      action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.REQUESTED, {
        payload: { shopId, itemId, address },
      })
    );
    const { response, error } = await api.getShippingFeeByAddressV4(
      shopId,
      itemId,
      address,
      isLite
    );
    if (!error && response && !response.error && response.data) {
      // convert array to map, to fit client side logic
      if (Array.isArray(response.data.logistic_service_types)) {
        response.data.logistic_service_types =
          response.data.logistic_service_types.reduce((prev: any, cur: any) => {
            prev[cur.name] = cur;
            return prev;
          }, {});
      }
      if (Array.isArray(response.data.promotion_rules)) {
        response.data.promotion_rules.forEach((rule: any) => {
          if (Array.isArray(rule.channels)) {
            rule.channels = rule.channels.reduce((prev: any, cur: any) => {
              prev[cur] = {};
              return prev;
            }, {});
          }
        }, {});
      }
      dispatch(
        action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.SUCCESS, {
          payload: { shopId, itemId, shipping: response.data },
        })
      );
    } else {
      dispatch(
        action(FETCH_PRODUCT_SHIPPING_BY_ADDRESS.FAILED, {
          payload: { shopId, itemId, error: error },
        })
      );
    }
  };
}

export const passItemDataFromList = (item: ItemBasic | ItemDetail) => {
  return {
    type: FETCH_PRODUCT.SUCCESS,
    payload: {
      itemId: item.itemid,
      item,
    },
  };
};

// eslint-disable-next-line
export function fetchProductShippingByAddressV5(
  shopId: number,
  itemId: number,
  address: {
    state: string;
    city: string;
    district?: string | undefined | null;
    town?: string | undefined | null;
    zipcode?: string | undefined | null;
  },
  isLite = false,
  modelId?: number,
  options?: {
    apm?: {
      apm_pid: string;
      apm_p: string;
      apm_ts: number;
      apm_fs?: boolean;
    };
  }
) {
  return async (dispatch: Dispatch, getState: GetState) =>
    await callApi(
      {
        apiCall: () =>
          api.getShippingFeeByAddressV5(
            shopId,
            itemId,
            address,
            isLite,
            modelId,
            options
          ),
        actions: [
          {
            type: FETCH_PRODUCT_SHIPPING.REQUESTED,
            payload: () => {
              return { shopId, itemId, modelId, address };
            },
          },
          {
            type: FETCH_PRODUCT_SHIPPING.SUCCESS,
            payload: (state, action, res) => {
              return { shopId, itemId, modelId, address, shipping: res };
            },
          },
          {
            type: FETCH_PRODUCT_SHIPPING.FAILED,
            payload: (state, action, res) => {
              return { shopId, itemId, modelId, address, error: res.error };
            },
          },
        ],
        shouldSkipCall: state => {
          if (modelId) {
            const shippingInfo = selectProductShippingInfoV5(
              state.item,
              itemId,
              modelId,
              address
            );
            return !!(
              shippingInfo &&
              (getIn(shippingInfo, ['API_STATUS', 'status']) ===
                API_STATUS.SUCCESS ||
                getIn(shippingInfo, ['API_STATUS', 'status']) ===
                  API_STATUS.REQUESTED)
            );
          }
          return false;
        },
      },
      dispatch,
      getState
    );
}

/**
 * Force fetch if ItemDetail is not in store; note that ItemBasic might alr be in item reducer
 * NOTE: To check whether ItemDetail is in store or not, we check the existence of "models" field
 *
 * @param {number} shopId
 * @param {number} itemId
 */
export function fetchItemDetailCached(
  shopId: number,
  itemId: number,
  getItemById: (
    state: any,
    itemId: number
  ) => ItemDetail | undefined | null = getItemByIdDefault
) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const oldState = getState();
    const oldItem = getItemById(oldState, itemId);
    if (!oldItem || oldItem.models === undefined) {
      await dispatch(
        fetchItem(shopId, itemId, {
          forceFetch: true,
          fromFlashSale: false,
        })
      );
    }
    const state = getState();
    const item = getItemById(state, itemId);
    return item;
  };
}
